import React from 'react';
import { Button, Dropdown, MenuProps, Space, Tag } from 'antd';
import { Moment } from 'moment';
import { History } from 'history';
import { Link } from 'react-router-dom';
import { standardSort } from '@shared/utils/standardSort';
import {
	CargoContractStatusTypes,
	CargoUnitLabels,
	DATE_AND_TIME,
	FixtureTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import { formatDate } from '@shared/utils/date';
import { Port } from '@api/utils/ports';
import { CargoDetails } from '@api/features/cargos/getCargoDetails';
import CargoContractStatusTag from '@client/screens/estimates/details/components/CargoContractStatusTag';
import { createEstimate } from '@client/lib/api';
import { Links } from '@client/utils/links';

type CargoDetailsWithEstimates = CargoDetails & {
	Estimates?: {
		id: number;
		estimateGroupId: number;
		readableId?: string | null;
		name?: string | null;
	}[];
};

type GetCargoMapColumnsParams = {
	history: History;
};

const getCargoMapColumns = ({ history }: GetCargoMapColumnsParams) => {
	const goToEstimate = (estimate: {
		id: number;
		estimateGroupId: number;
	}) => {
		history.push(
			Links.Fixtures[FixtureTypes.SPOT].Details.get(
				estimate.estimateGroupId,
				estimate.id,
			),
		);
	};

	const createEstimateAndGo = async (cargoId: number) => {
		const estimate = await createEstimate(
			{
				currency: 'USD',
				name: '',
			},
			undefined,
			false,
			cargoId,
		);

		goToEstimate(estimate);
	};

	const renderEstimateAction = (row: CargoDetailsWithEstimates) => {
		const estimates = row.Estimates ?? [];

		if (estimates.length === 0) {
			return (
				<Button type="link" onClick={() => createEstimateAndGo(row.id)}>
					Create
				</Button>
			);
		}

		if (estimates.length === 1) {
			return (
				<Button type="link" onClick={() => goToEstimate(estimates[0])}>
					{estimates[0].name || estimates[0].readableId || estimates[0].id}
				</Button>
			);
		}

		const items: MenuProps['items'] = estimates.map((estimate) => ({
			key: estimate.id,
			label: estimate.name || estimate.readableId || `Estimate ${estimate.id}`,
			onClick: () => goToEstimate(estimate),
		}));

		return (
			<Dropdown menu={{ items }} trigger={['click']}>
				<Button type="link">
					{estimates.length} estimates
				</Button>
			</Dropdown>
		);
	};

	return [
		{
			title: 'Contract status',
			key: 'contractStatus',
			dataIndex: 'contractStatus',
			sorter: standardSort('contractStatus'),
			render: (status: Values<typeof CargoContractStatusTypes>) => (
				<CargoContractStatusTag status={status} />
			),
			width: 130,
			align: 'center' as const,
		},
		{
			title: 'Charterer',
			dataIndex: 'chartererName',
			key: 'chartererName',
			sorter: standardSort('chartererName'),
			ellipsis: true,
			width: 140,
			render: (chartererName: string) => chartererName ?? '—',
			align: 'center' as const,
		},
		{
			title: 'Cargo name',
			dataIndex: 'type',
			key: 'type',
			sorter: standardSort('type'),
			ellipsis: true,
			width: 140,
			render: (type: string, row: CargoDetails) => (
				<Link to={Links.Cargo.get(row.id)}>
					{type ?? '—'}
				</Link>
			),
			align: 'center' as const,
		},
		{
			title: 'CP Quantity',
			dataIndex: 'cpQuantity',
			key: 'cpQuantity',
			sorter: standardSort('cpQuantity'),
			width: 110,
			render: (_: number, row: CargoDetails) => (
				row.quantity != null ?
					`${row.quantity} ${CargoUnitLabels[row.unit]}` :
					'—'
			),
			align: 'center' as const,
		},
		{
			title: 'Laycan from',
			dataIndex: 'firstLaycan',
			key: 'firstLaycan',
			sorter: standardSort('firstLaycan'),
			width: 110,
			render: (firstLaycan: Moment | null) => (
				firstLaycan != null ?
					formatDate(firstLaycan, DATE_AND_TIME) :
					'—'
			),
			align: 'center' as const,
		},
		{
			title: 'Laycan to',
			dataIndex: 'lastLaycan',
			key: 'lastLaycan',
			sorter: standardSort('lastLaycan'),
			width: 110,
			render: (lastLaycan: Moment | null) => (
				lastLaycan != null ?
					formatDate(lastLaycan, DATE_AND_TIME) :
					'—'
			),
			align: 'center' as const,
		},
		{
			title: 'Load ports',
			dataIndex: 'loadingPorts',
			key: 'loadingPorts',
			sorter: standardSort('loadingPorts'),
			render: (ports: Array<Port>) => (
				ports?.length ? (
					<Space size={2} wrap>
						{ports.map((p) => (
							<Tag key={p.id} color="geekblue">
								{p.name}
							</Tag>
						))}
					</Space>
				) : '—'
			),
			width: 160,
			align: 'center' as const,
		},
		{
			title: 'Disch ports',
			dataIndex: 'dischargePorts',
			key: 'dischargePorts',
			sorter: standardSort('dischargePorts'),
			render: (ports: Array<Port>) => (
				ports?.length ? (
					<Space size={2} wrap>
						{ports.map((p) => (
							<Tag key={p.id} color="red">
								{p.name}
							</Tag>
						))}
					</Space>
				) : '—'
			),
			width: 160,
			align: 'center' as const,
		},
		{
			title: 'Freight rate',
			dataIndex: 'freightRate',
			key: 'freightRate',
			sorter: standardSort('freightRate'),
			width: 130,
			render: (_: unknown, row: CargoDetails) => {
				if (!row.freightRate || !row.currency) {
					return '—';
				}

				return formatCurrency(row.freightRate, row.currency);
			},
			align: 'center' as const,
		},
		{
			title: 'Estimates',
			key: 'estimates',
			width: 120,
			ellipsis: true,
			render: (_: unknown, row: CargoDetailsWithEstimates) => (
				renderEstimateAction(row)
			),
			align: 'center' as const,
		},
	];
};

export default getCargoMapColumns;